import React from 'react';
import { Link } from 'react-router-dom';
import { INDUSTRY_LIST } from '@/data/industries';
import { Button } from '@/components/ui/button';
import { ArrowRight, Ship, Warehouse, Cog, Truck } from 'lucide-react';

const iconMap = {
  'ports-terminals': Ship,
  'warehousing-distribution': Warehouse,
  'manufacturing': Cog,
  'logistics-supply-chain': Truck,
};

export default function IndustriesPage() {
  return (
    <div>
      <section className="relative py-24 md:py-28 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 viso-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-4">Industries</div>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-4xl leading-[1.05]">
            <span className="text-gradient">Built for how your operation actually runs.</span>
          </h1>
          <p className="text-slate-400 mt-5 max-w-2xl">
            From the gate to the line to the last mile — AI vision tuned to four industries, not a generic camera add-on.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 space-y-5">
          {INDUSTRY_LIST.map((ind) => {
            const Icon = iconMap[ind.slug] || Ship;
            return (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}`}
                className="group grid md:grid-cols-12 gap-6 rounded-2xl border border-white/10 overflow-hidden bg-[#0a0d12] hover:border-cyan-400/30 transition-colors"
              >
                <div className="md:col-span-5 relative min-h-[240px]">
                  <img src={ind.poster} alt={ind.kicker} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d12] via-[#0a0d12]/40 to-transparent" />
                  <div className="absolute inset-0 viso-grid opacity-30" />
                </div>
                <div className="md:col-span-7 p-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-cyan-300">
                      {ind.num} · {ind.kicker}
                    </div>
                  </div>
                  <h2 className={`font-display text-3xl md:text-4xl font-bold mt-3 bg-gradient-to-r ${ind.accent} bg-clip-text text-transparent`}>
                    {ind.title}
                  </h2>
                  <p className="text-slate-300 mt-3">{ind.subtitle}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {ind.pills.map((p) => (
                      <span key={p} className="px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider bg-white/5 border border-white/10 text-slate-300">
                        {p}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-1 text-sm text-cyan-300 group-hover:gap-2 transition-all">
                    Explore industry <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/5 py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl md:text-3xl font-bold">Bring a feed. We&apos;ll read it live.</div>
            <p className="text-slate-400 mt-2 max-w-xl">Every industry, one platform — running on the CCTV already in place.</p>
          </div>
          <Link to="/contact">
            <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
              Book a demo <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
